/**
 * Centralized URL Configuration
 * 
 * All URLs used throughout the application should be defined here
 * to make it easy to switch between development and production environments.
 */

import { config } from '@/config/config';

// Application URLs
export const APP_URL = config.app.url;
export const API_URL = config.api.baseUrl;
export const WS_URL = config.websocket.url;

// Backend Configuration
export const BACKEND_HOST = config.backend.host;
export const BACKEND_PORT = config.backend.port;
export const BACKEND_URL = config.backend.url;

// Frontend Configuration
export const FRONTEND_HOST = config.frontend.host;
export const FRONTEND_PORT = config.frontend.port;

// External Service URLs
export const AVATAR_API_URL = config.external.avatarApiUrl;

// Parse allowed domains from environment variable
export const ALLOWED_DOMAINS = config.security.allowedDomains;

// External service domains for CSP
export const STRIPE_DOMAINS = config.payments.stripe.domains;

export const STYTCH_DOMAINS = config.auth.stytch.domain;

export const SENTRY_DOMAINS = config.monitoring.sentry.domains;

// Helper function to get the API base URL (without /api suffix)
export function getApiBaseUrl(): string {
  return API_URL.replace(/\/api\/?$/, '');
}

// Helper function to check if a URL is allowed
export function isAllowedDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const origin = urlObj.origin;
    
    return ALLOWED_DOMAINS.some(allowed => {
      // Handle wildcards
      if (allowed.includes('*')) {
        const pattern = allowed
          .replace(/\./g, '\\.')
          .replace(/\*/g, '.*');
        return new RegExp(`^${pattern}$`).test(origin);
      }
      return origin === allowed;
    });
  } catch {
    return false;
  }
}

// Helper function to generate CSP connect-src directive
export function getCSPConnectSrc(): string {
  const connectSources = [
    "'self'",
    ...ALLOWED_DOMAINS,
    ...STRIPE_DOMAINS,
    ...STYTCH_DOMAINS,
    ...SENTRY_DOMAINS,
    // WebSocket protocols
    ...ALLOWED_DOMAINS.map(domain => domain.replace('http://', 'ws://')),
    ...ALLOWED_DOMAINS.map(domain => domain.replace('https://', 'wss://')),
  ];
  
  return [...new Set(connectSources)].join(' ');
}

// Environment checks
export const isDevelopment = config.env === 'development';
export const isProduction = config.env === 'production';
export const isTest = config.env === 'test';

// Testing configuration
export const TEST_BASE_URL = config.backend.url;